/*----------------------------------
- LANDING: HOME ARRIVALS
----------------------------------*/

/**
 * Switches on the marketing layer's arrivals on Home (decision 15 of the batch 7 report): `html.js` arms the sheets'
 * hide-then-rise rules (`.js .rv` in marketing.css and home.css; `html.js [data-reveal]` in shared.css on the pages that still load it, which Home no longer does), and each `.rv`
 * takes `.in` as it scrolls into view. Without this script the server's HTML reads whole (`html:not(.js)` in home.css).
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0110
 * @rule Content painted by the server is never hidden again: the elements already on screen are marked arrived
 *       (`.in`, `.is-revealed`) BEFORE `js` is added, the same order as `initScrollReveal` (reveal.ts), so the hero and
 *       the demo window are never blanked, and the LCP image never waits on hydration.
 * @rule Under reduced motion or the kill switch every element arrives at once (`motionAllowed`).
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { motionAllowed } from '@uniquedomains/components/marketing/motion/motionAllowed';

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** The class the sheets key their arrivals on, on `<html>` (shared with reveal.ts's `REVEAL_ENABLED_CLASS`). */
const SCRIPT_ENABLED_CLASS = 'js';

/** An element arrives once its top passes the lower fifth of the viewport, as reveal.ts does. */
const ARRIVAL_ROOT_MARGIN = '0px 0px -20% 0px';

/*----------------------------------
- HELPERS
----------------------------------*/

const isOnScreen = (element: Element): boolean => {
    const rect = element.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
};

const arrive = (element: Element) => element.classList.add(element.classList.contains('rv') ? 'in' : 'is-revealed');

/*----------------------------------
- PUBLIC API
----------------------------------*/

/**
 * Starts the arrivals under `root` (Home's `.ud-home`); returns the cleanup. The feature cards and the funnel stages
 * mark themselves too (useCardStack, useFunnelMotion): a second `.in` changes nothing.
 */
export const startHomeArrivals = (root: HTMLElement): (() => void) => {
    const elements = Array.from(root.querySelectorAll('.rv, [data-reveal]'));
    const instant = !motionAllowed() || typeof IntersectionObserver === 'undefined';
    const pending = instant ? [] : elements.filter((element) => !isOnScreen(element));
    elements.filter((element) => !pending.includes(element)).forEach(arrive);
    document.documentElement.classList.add(SCRIPT_ENABLED_CLASS);
    if (!pending.length) return () => undefined;

    const observer = new IntersectionObserver(
        (entries) =>
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                arrive(entry.target);
                observer.unobserve(entry.target);
            }),
        { rootMargin: ARRIVAL_ROOT_MARGIN, threshold: 0 },
    );
    pending.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
};
